import React from "react";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SEO_LEGAL_PRIVACY, SEO_LEGAL_TERMS, SEO_LEGAL_COOKIE } from "@/lib/seo-defs";
import { Badge } from "@/components/ui/badge";
import { Info } from "lucide-react";

// NOTE: Tutti i testi sono PLACEHOLDER conformi alle linee guida GDPR/AGCOM.
// Vanno revisionati da un legale (DPO + studio legale) prima della pubblicazione.
// Versione: 2026-06-13 - v0.9 (DRAFT)

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    body: `**Titolare del trattamento:** CXOORA Srl (placeholder - sostituire con ragione sociale completa), sede legale Via [...], P.IVA [...] - PEC: cxoora@pec.it - email: privacy@cxoora.com

Ai sensi del Regolamento UE 2016/679 (GDPR) e del D.lgs. 196/2003 (Codice Privacy) così come modificato dal D.lgs. 101/2018, CXOORA tratta i dati personali raccolti tramite il sito, il check-up e le funzioni della piattaforma esclusivamente per le finalità di erogazione del servizio.

**1. Categorie di dati trattati**
- Dati identificativi (nome, cognome, email aziendale, telefono).
- Dati professionali/aziendali (ruolo, settore, dimensione, ricavi, esperienze).
- Dati di profilazione manager (competenze, disponibilità, rate).
- Documenti caricati dall'utente nel contesto dei progetti.
- Dati di utilizzo (log, indirizzi IP anonimizzati, cookie tecnici).
- Eventuali dati di pagamento gestiti tramite Stripe (PCI-DSS compliant).

**2. Finalità del trattamento**
- Erogazione del servizio di matching e gestione progetti.
- Adempimenti contrattuali e fiscali.
- Comunicazioni di servizio (notifiche progetto, scadenze, fatturazione).
- Newsletter e marketing (solo con consenso esplicito separato).
- Analisi statistiche aggregate per il miglioramento del servizio.

**3. Base giuridica**
- Esecuzione del contratto (art. 6.1.b GDPR).
- Consenso esplicito (art. 6.1.a) per newsletter e cookie di profilazione.
- Legittimo interesse (art. 6.1.f) per sicurezza e antifrode.
- Obblighi di legge (art. 6.1.c) per fatturazione e contabilità.

**4. Conservazione**
- Account attivi: per tutta la durata del rapporto + 10 anni (obbligo fiscale).
- Dati di candidatura non accettata: 24 mesi.
- Log di sicurezza: 12 mesi.
- Newsletter: fino a revoca consenso.

**5. Destinatari**
- Personale autorizzato CXOORA (dipendenti, collaboratori).
- Manager/aziende parte di progetti condivisi (solo dati strettamente necessari).
- Fornitori IT (hosting, object storage, email): nominati Responsabili ex art. 28 GDPR.
- Autorità competenti su richiesta motivata.

**6. Trasferimenti extra-UE**
Alcuni fornitori (es. Google per Calendar/Meet, se attivato) potrebbero trasferire dati negli USA. Tali trasferimenti avvengono sulla base delle Standard Contractual Clauses approvate dalla Commissione UE.

**7. Diritti dell'interessato**
Hai diritto di accedere, rettificare, cancellare, limitare il trattamento, opporti, richiedere la portabilità dei dati. Puoi anche revocare il consenso in qualsiasi momento. Per esercitare i tuoi diritti: **privacy@cxoora.com**.

Hai inoltre diritto di proporre reclamo all'Autorità Garante per la protezione dei dati personali (www.garanteprivacy.it).`,
  },
  cookie: {
    title: "Cookie Policy",
    body: `Il presente documento illustra l'uso di cookie e tecnologie simili sul sito CXOORA, in conformità alla normativa europea (GDPR + Direttiva ePrivacy) e ai Provvedimenti del Garante Privacy italiano del 10/06/2021.

**Cosa sono i cookie**
I cookie sono piccoli file di testo memorizzati nel browser dell'utente. Permettono al sito di funzionare correttamente, ricordare preferenze, raccogliere statistiche.

**Categorie utilizzate**

🔒 **1. Cookie tecnici essenziali (sempre attivi)**
- Sessione di autenticazione JWT (cxoora_token).
- Preferenza lingua (cxoora_lang).
- Stato consenso cookie (cxoora_cookie_consent).
*Base giuridica: legittimo interesse - non richiedono consenso.*

📊 **2. Cookie di analytics (opzionali)**
- Misurazione anonima delle visite e dei flussi utente.
- Eventuale uso di Google Analytics 4 in modalità anonimizzata (quando attivato).
*Base giuridica: consenso esplicito.*

🔌 **3. Cookie di terze parti per servizi integrati (opzionali)**
- Google Calendar/Meet (se l'utente attiva l'integrazione OAuth).
- Stripe (gestione pagamenti, quando attivati in produzione).
*Base giuridica: consenso esplicito + necessità per il servizio richiesto.*

**Gestione del consenso**
Al primo accesso compare un banner che permette di:
- Accettare tutti i cookie.
- Rifiutare i cookie opzionali (solo essenziali).
- Personalizzare le preferenze.

Puoi sempre modificare le tue scelte cliccando sul link "Preferenze cookie" nel footer.

**Disabilitazione tramite browser**
Tutti i principali browser permettono di gestire i cookie dalle impostazioni:
- Chrome, Firefox, Safari, Edge: vedere documentazione ufficiale.

**Durata**
- Cookie di sessione: cancellati alla chiusura del browser.
- Cookie persistenti: massimo 12 mesi.

Per ulteriori informazioni: **privacy@cxoora.com**.`,
  },
  termini: {
    title: "Termini e Condizioni di Servizio",
    body: `I presenti termini regolano l'utilizzo della piattaforma CXOORA. Registrandoti o utilizzando il servizio, accetti integralmente quanto segue.

**1. Definizioni**
- "Piattaforma": il sito e l'applicazione CXOORA.
- "Azienda Cliente": società che richiede un Fractional Manager.
- "Manager": professionista iscritto al network CXOORA.
- "CXOORA": il gestore della piattaforma (CXOORA Srl).

**2. Accesso al servizio**
- Riservato a soggetti maggiorenni e ad aziende regolarmente costituite (Italia/UE).
- È richiesta la registrazione con credenziali personali e veritiere.
- È vietato condividere l'account con terzi.

**3. Ruolo di CXOORA**
CXOORA opera come intermediario qualificato:
- Valida i profili dei manager (verifica esperienze, referenze, badge).
- Effettua il matching tra esigenze aziendali e competenze manageriali.
- Cura una shortlist (max 3 profili) per ogni richiesta.
- Mette a disposizione strumenti di project management.

CXOORA **non** è parte del contratto di consulenza che eventualmente sarà sottoscritto tra Azienda e Manager. Resta tuttavia responsabile della qualità della selezione e del processo.

**4. Pacchetti e proposte commerciali**
- Le tariffe sono indicate nelle proposte commerciali generate per ciascuna richiesta.
- I pacchetti standard sono: Fractional Leadership, Project Sprint, Advisory & Coaching.
- Eventuali personalizzazioni sono indicate caso per caso.

**5. Fatturazione e pagamenti**
- Le fatture sono emesse mensilmente sulla base degli accordi pattuiti.
- I pagamenti sono gestiti tramite Stripe (PCI-DSS) o bonifico bancario.
- Eventuali ritardi superiori a 30 gg comportano la sospensione del servizio.

**6. Obblighi del Manager**
- Rispettare il Codice Etico CXOORA.
- Mantenere riservatezza assoluta sui dati aziendali.
- Aggiornare KPI e milestone nel workspace di progetto.
- Comunicare tempestivamente eventuali conflitti di interesse.

**7. Obblighi dell'Azienda**
- Fornire informazioni veritiere nel check-up.
- Garantire al Manager l'accesso a informazioni e team necessari.
- Validare KPI mensili e milestone secondo timeline concordata.

**8. Responsabilità**
CXOORA seleziona i manager con cura ma non garantisce risultati specifici. La responsabilità per i risultati di business resta in capo all'Azienda e al Manager nel rapporto consulenziale.

**9. Recesso e cessazione**
- Recesso aziendale: preavviso di 30 giorni.
- Recesso CXOORA: solo per grave inadempimento o cessazione attività, con preavviso di 60 giorni.

**10. Foro competente**
Per ogni controversia è competente in via esclusiva il Foro di [città sede legale - placeholder].

**11. Modifiche**
CXOORA può aggiornare i presenti termini comunicando le modifiche via email almeno 30 giorni prima dell'entrata in vigore.

Ultimo aggiornamento: 2026-06-13`,
  },
  "codice-etico": {
    title: "Codice Etico CXOORA",
    body: `Il presente codice etico definisce i principi che ogni manager, azienda e collaboratore della rete CXOORA si impegna a rispettare.

**1. Riservatezza**
Tutela rigorosa delle informazioni aziendali ricevute durante la collaborazione. Il manager si impegna a non divulgare a terzi dati strategici, finanziari, commerciali o tecnici, anche dopo la conclusione del rapporto.

**2. Trasparenza e conflitti di interesse**
Ogni manager deve dichiarare preventivamente:
- Eventuali rapporti con concorrenti dell'azienda cliente.
- Partecipazioni societarie rilevanti.
- Altri incarichi che possano interferire con la disponibilità dichiarata.

**3. Professionalità**
- Rispetto degli impegni assunti (calendario, ore dedicate, deliverable).
- Comunicazione tempestiva e chiara con azienda e team CXOORA.
- Qualità del lavoro all'altezza del livello dichiarato in candidatura.
- Aggiornamento continuo delle proprie competenze.

**4. Inclusione e non discriminazione**
- Trattamento equo di tutte le persone coinvolte, indipendentemente da genere, età, origine, religione, orientamento sessuale, disabilità.
- Promozione attiva di ambienti di lavoro rispettosi.

**5. Sostenibilità**
- Attenzione agli impatti sociali, economici e ambientali delle decisioni proposte.
- Promozione di pratiche di business responsabili in linea con gli Obiettivi di Sviluppo Sostenibile ONU.

**6. Integrità e anticorruzione**
- Divieto assoluto di accettare o offrire regali, vantaggi o pagamenti indebiti.
- Trasparenza nelle relazioni con fornitori, clienti, pubbliche amministrazioni.
- Rispetto della normativa anticorruzione (L. 190/2012, D.lgs. 231/2001).

**7. Tutela dei dati**
- Gestione dei dati personali nel rigoroso rispetto del GDPR.
- Utilizzo dei sistemi CXOORA per ogni comunicazione operativa.
- Mai trasferire documenti aziendali su canali personali non autorizzati.

**8. Segnalazioni**
Eventuali violazioni del Codice Etico possono essere segnalate in modalità riservata a: **etica@cxoora.com**. CXOORA garantisce tutela del segnalante in buona fede.

Ultimo aggiornamento: 2026-06-13`,
  },
};

export default function LegalPage({ type = "privacy" }) {
  const data = CONTENT[type] || CONTENT.privacy;
  const seo = type === "termini" ? SEO_LEGAL_TERMS
            : type === "cookie" ? SEO_LEGAL_COOKIE
            : SEO_LEGAL_PRIVACY;
  return (
    <div className="min-h-screen">
      <SEO {...seo} />
      <PublicHeader />
      <section className="pt-28 pb-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Badge className="bg-amber-50 text-amber-700 border-0 hover:bg-amber-50 inline-flex items-center gap-1.5" data-testid="legal-draft-badge">
            <Info size={12} /> Bozza da sottoporre a verifica legale
          </Badge>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-900" data-testid="legal-title">{data.title}</h1>
          <div
            className="mt-6 prose prose-slate max-w-none whitespace-pre-line text-slate-700 leading-relaxed"
            data-testid="legal-content"
          >
            {data.body}
          </div>
          <p className="mt-12 text-xs text-slate-400" data-testid="legal-version">Versione bozza 0.9 — 2026-06-13 — Richiede revisione da consulente legale prima della pubblicazione definitiva.</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
